/**
 * Login — écran de choix (Pasteur / Fidèle) puis formulaire email + mot de passe.
 * Le rôle réel est déterminé par le backend ; le choix initial est une UX rassurante.
 */
import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import Input from "../components/Input";
import Button from "../components/Button";
import Logo from "../components/Logo";
import WhatsAppFAB from "../components/WhatsAppFAB";

const ROLE_CONFIG = {
  pasteur: {
    icon: "⛪",
    label: "Pasteur",
    title: "Je suis Pasteur / Responsable",
    desc: "Gérer mon église, mes membres, mes finances",
    accent: colors.gold,
  },
  fidele: {
    icon: "🙏",
    label: "Fidèle",
    title: "Je suis Fidèle / Membre",
    desc: "Voir mes infos, mes dons, les événements de mon église",
    accent: colors.blue,
  },
};

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [choice, setChoice] = useState(null); // null | "pasteur" | "fidele"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.ok) {
        toast.success(`Bienvenue ${result.user.full_name} !${result.mock ? " (mode démo)" : ""}`);
        const isAdmin = ["pasteur", "secretaire", "admin"].includes(result.user.role);
        navigate(isAdmin ? "/dashboard" : "/profil");
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (err) {
      const msg = err.message || "Erreur inattendue";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (type) => {
    if (type === "admin") { setEmail("admin@monministere.com"); setPassword("admin123"); }
    else { setEmail("fidele@monministere.com"); setPassword("fidele123"); }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(circle at 15% 0%,   rgba(228,167,44,0.10) 0%, transparent 50%),
          radial-gradient(circle at 85% 100%, rgba(96,165,250,0.06) 0%, transparent 50%),
          ${colors.bg}
        `,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: fonts.body,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: "20px",
          padding: "40px 32px",
          boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
        }}
      >
        <Link to="/" style={{ textDecoration: "none", display: "block", marginBottom: "32px" }}>
          <Logo size="lg" showName showTagline centered />
        </Link>

        {choice === null ? (
          <ChooserScreen onPick={setChoice} />
        ) : (
          <div className="fade-in">
            <RoleBadge choice={choice} onChange={() => { setChoice(null); setError(null); }} />

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Input
                label="Adresse email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                required
                icon="✉"
                autoComplete="email"
              />
              <Input
                label="Mot de passe"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                icon="🔒"
                autoComplete="current-password"
              />

              {error && (
                <div style={{
                  background: "rgba(224,91,106,0.1)",
                  border: `1px solid ${colors.rose}`,
                  color: colors.rose,
                  padding: "10px 14px",
                  borderRadius: "10px",
                  fontSize: "13px",
                }}>
                  {error}
                </div>
              )}

              <Button type="submit" fullWidth size="lg" loading={submitting}>
                {submitting ? "Connexion..." : "Se connecter"}
              </Button>
            </form>

            {import.meta.env.VITE_ENV !== "production" && import.meta.env.MODE !== "production" && (
              <div style={{
                marginTop: "24px",
                padding: "16px",
                background: colors.card,
                borderRadius: "12px",
                border: `1px solid ${colors.border}`,
              }}>
                <div style={{
                  fontSize: "11px",
                  color: colors.textMuted,
                  marginBottom: "10px",
                  textAlign: "center",
                  letterSpacing: "0.1em",
                  fontWeight: 600,
                }}>
                  COMPTES DÉMO
                </div>
                <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                  <Button variant="ghost" size="sm" fullWidth onClick={() => fillDemo("admin")}>
                    👨‍💼 Administrateur (Pasteur)
                  </Button>
                  <Button variant="ghost" size="sm" fullWidth onClick={() => fillDemo("fidele")}>
                    👤 Fidèle
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <p style={{ marginTop: "20px", textAlign: "center", fontSize: "13px", color: colors.textMuted }}>
          Pas encore de compte ?{" "}
          <Link to="/inscription" style={{ color: colors.gold, fontWeight: 600, textDecoration: "none" }}>
            Commencer gratuitement →
          </Link>
        </p>

        <p style={{ marginTop: "16px", textAlign: "center", fontSize: "11px", color: colors.textDim, letterSpacing: "0.05em" }}>
          © 2026 Mon Ministère · Pour Sa gloire
        </p>
      </div>
      <WhatsAppFAB />
    </div>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────

function ChooserScreen({ onPick }) {
  return (
    <div className="fade-in" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <h2 style={{ fontFamily: fonts.heading, fontSize: 22, fontWeight: 700, textAlign: "center", marginBottom: 4 }}>
        Comment souhaitez-vous vous connecter&nbsp;?
      </h2>
      <p style={{ color: colors.textMuted, fontSize: 13, textAlign: "center", marginBottom: 8 }}>
        Sélectionnez votre profil pour continuer
      </p>
      <RoleCard role="pasteur" onClick={() => onPick("pasteur")} />
      <RoleCard role="fidele" onClick={() => onPick("fidele")} />
    </div>
  );
}

function RoleCard({ role, onClick }) {
  const cfg = ROLE_CONFIG[role];
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "18px 20px",
        background: hover ? `${cfg.accent}18` : colors.card,
        border: `1.5px solid ${hover ? cfg.accent : colors.border}`,
        borderRadius: 14,
        textAlign: "left",
        cursor: "pointer",
        transition: "all 200ms",
        transform: hover ? "translateY(-1px)" : "none",
      }}
    >
      <span style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{cfg.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: colors.text, fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{cfg.title}</div>
        <div style={{ color: colors.textMuted, fontSize: 12, lineHeight: 1.4 }}>{cfg.desc}</div>
      </div>
      <span style={{ color: hover ? cfg.accent : colors.textMuted, fontSize: 20 }}>→</span>
    </button>
  );
}

function RoleBadge({ choice, onChange }) {
  const cfg = ROLE_CONFIG[choice];
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "10px 14px",
      background: colors.card,
      border: `1px solid ${cfg.accent}40`,
      borderRadius: 10,
      marginBottom: 20,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 20 }}>{cfg.icon}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: cfg.accent }}>
          Connexion — {cfg.label}
        </span>
      </div>
      <button
        type="button"
        onClick={onChange}
        style={{ background: "transparent", color: colors.textMuted, fontSize: 12, fontWeight: 500, cursor: "pointer" }}
      >
        Changer
      </button>
    </div>
  );
}
